import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import Icon from '../../../components/AppIcon'
import { api } from '../../../lib/apiClient'
import { useToast } from '../../../lib/ToastContext'

const INITIAL_LIMIT = 280
const PAGE_LIMIT = 180

function summarizeData(value, max = 160) {
  if (value == null) return ''
  try {
    const raw = typeof value === 'string' ? value : JSON.stringify(value)
    return raw.length > max ? `${raw.slice(0, max)}…` : raw
  } catch {
    return '[unserializable data]'
  }
}

function toSearchableText(row = {}) {
  return [
    row?.type,
    row?.action,
    row?.message,
    row?.path,
    row?.route,
    row?.url,
    row?.method,
    row?.email,
    row?.uid,
    row?.role,
    row?.source,
    row?.level,
    row?.severity,
    summarizeData(row?.data, 240)
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())
    .join(' ')
}

function severityClasses(severity = 'green') {
  if (severity === 'red') {
    return 'bg-destructive/15 text-destructive border border-destructive/30'
  }
  if (severity === 'yellow') {
    return 'bg-warning/15 text-warning border border-warning/30'
  }
  return 'bg-success/15 text-success border border-success/30'
}

function formatWhen(row = {}) {
  const ms = Number(row?.createdAtMs || 0)
  if (!Number.isFinite(ms) || ms <= 0) return 'Pending timestamp'
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

function actorLabel(row = {}) {
  if (row?.email) return row.email
  if (row?.uid) return row.uid
  return 'system'
}

function mergeRows(existing, incoming) {
  const merged = new Map()
  for (const row of existing || []) merged.set(row.id, row)
  for (const row of incoming || []) merged.set(row.id, row)
  return Array.from(merged.values()).sort((a, b) => Number(b?.createdAtMs || 0) - Number(a?.createdAtMs || 0))
}

export default function AppLogPanel() {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [nextCursorMs, setNextCursorMs] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [filters, setFilters] = useState({
    keyword: '',
    severity: '',
    actor: '',
    source: '',
    action: ''
  })

  const loadInitialLogs = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listLogs({
        limit: INITIAL_LIMIT
      })
      const items = Array.isArray(res?.items) ? res.items : []
      const next = Number(res?.nextCursorMs || 0) || null
      setRows(mergeRows([], items))
      setNextCursorMs(next)
      setHasMore(Boolean(next) && items.length >= INITIAL_LIMIT)
    } catch (err) {
      console.error('Failed to load app logs:', err)
      toast.error(err?.message || 'Failed to load app logs')
    } finally {
      setLoading(false)
    }
  }, [toast])

  const loadMoreLogs = useCallback(async () => {
    if (loadingMore || !nextCursorMs) return
    setLoadingMore(true)
    try {
      const res = await api.listLogs({
        limit: PAGE_LIMIT,
        beforeMs: nextCursorMs
      })
      const items = Array.isArray(res?.items) ? res.items : []
      const next = Number(res?.nextCursorMs || 0) || null
      setRows((prev) => mergeRows(prev, items))
      setNextCursorMs(next)
      setHasMore(Boolean(next) && items.length >= PAGE_LIMIT)
    } catch (err) {
      console.error('Failed to load older app logs:', err)
      toast.error(err?.message || 'Failed to load older logs')
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, nextCursorMs, toast])

  useEffect(() => {
    loadInitialLogs()
  }, [loadInitialLogs])

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const keyword = String(filters.keyword || '').trim().toLowerCase()
      const actor = String(filters.actor || '').trim().toLowerCase()
      const source = String(filters.source || '').trim().toLowerCase()
      const action = String(filters.action || '').trim().toLowerCase()
      const severity = String(filters.severity || '').trim().toLowerCase()

      if (severity && row.severity !== severity) return false
      if (source && !String(row.source || '').toLowerCase().includes(source)) return false
      if (action) {
        const actionText = [row.action, row.type].filter(Boolean).join(' ').toLowerCase()
        if (!actionText.includes(action)) return false
      }
      if (actor) {
        const actorText = [row.email, row.uid, row.role].filter(Boolean).join(' ').toLowerCase()
        if (!actorText.includes(actor)) return false
      }
      if (keyword && !toSearchableText(row).includes(keyword)) return false
      return true
    })
  }, [rows, filters])

  const summary = useMemo(() => {
    return filteredRows.reduce((acc, row) => {
      const sev = row?.severity || 'green'
      if (sev === 'red') acc.red += 1
      else if (sev === 'yellow') acc.yellow += 1
      else acc.green += 1
      return acc
    }, { red: 0, yellow: 0, green: 0 })
  }, [filteredRows])

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      keyword: '',
      severity: '',
      actor: '',
      source: '',
      action: ''
    })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Card variant="solid">
        <CardHeader>
          <CardTitle>App Log</CardTitle>
          <CardDescription>Track app activity, risk actions, and execution history across client, server, and scheduled jobs.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
              <Input
                label="Keyword"
                placeholder="delete, barcode, 03:00, product id..."
                value={filters.keyword}
                onChange={(e) => onFilterChange('keyword', e.target.value)}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground/80">Severity</label>
                <select
                  value={filters.severity}
                  onChange={(e) => onFilterChange('severity', e.target.value)}
                  className="h-10 w-full rounded-xl border border-border bg-muted/40 px-3.5 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20 transition-all"
                >
                  <option value="">All</option>
                  <option value="red">Red (Risk)</option>
                  <option value="yellow">Yellow (Caution)</option>
                  <option value="green">Green (Normal)</option>
                </select>
              </div>
              <Input
                label="Actor"
                placeholder="email, uid, role..."
                value={filters.actor}
                onChange={(e) => onFilterChange('actor', e.target.value)}
              />
              <Input
                label="Source"
                placeholder="client, server, function, scheduler..."
                value={filters.source}
                onChange={(e) => onFilterChange('source', e.target.value)}
              />
              <Input
                label="Action"
                placeholder="delete_product, delist_product..."
                value={filters.action}
                onChange={(e) => onFilterChange('action', e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-lg bg-destructive/15 text-destructive border border-destructive/30">
                  Red {summary.red}
                </span>
                <span className="text-xs px-2 py-1 rounded-lg bg-warning/15 text-warning border border-warning/30">
                  Yellow {summary.yellow}
                </span>
                <span className="text-xs px-2 py-1 rounded-lg bg-success/15 text-success border border-success/30">
                  Green {summary.green}
                </span>
                <span className="text-xs text-muted-foreground">
                  Showing {filteredRows.length} of {rows.length} loaded logs
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="Eraser" onClick={clearFilters}>
                  Clear filters
                </Button>
                <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadInitialLogs}>
                  Refresh
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border/60 overflow-hidden">
              <div className="overflow-auto max-h-[520px]">
                <table className="w-full text-sm min-w-[880px]">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="text-left border-b border-border/60">
                      <th className="px-3 py-2 font-semibold text-foreground">When</th>
                      <th className="px-3 py-2 font-semibold text-foreground">Severity</th>
                      <th className="px-3 py-2 font-semibold text-foreground">Actor</th>
                      <th className="px-3 py-2 font-semibold text-foreground">Action</th>
                      <th className="px-3 py-2 font-semibold text-foreground">Source</th>
                      <th className="px-3 py-2 font-semibold text-foreground">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {loading && (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                          <Icon name="Loader2" size={16} className="inline-block mr-2 animate-spin" />
                          Loading logs...
                        </td>
                      </tr>
                    )}

                    {!loading && filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                          No logs found for the current filters.
                        </td>
                      </tr>
                    )}

                    {!loading && filteredRows.map((row) => {
                      const details = row.message || row.path || row.route || row.url || summarizeData(row.data, 120) || '-'
                      return (
                        <tr key={row.id} className="align-top hover:bg-muted/30 transition-colors">
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatWhen(row)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityClasses(row.severity)}`}>
                              {String(row.severity || 'green').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-foreground">{actorLabel(row)}</div>
                            <div className="text-xs text-muted-foreground">{row.role || '-'}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-foreground font-medium">{row.action || row.type || 'event'}</div>
                            <div className="text-xs text-muted-foreground">{row.method || row.type || '-'}</div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            <div>{row.source || '-'}</div>
                            <div className="text-xs">{row.level || '-'}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-foreground">{details}</div>
                            {row.data && (
                              <div className="text-xs text-muted-foreground mt-1 font-mono break-all">
                                {summarizeData(row.data, 180)}
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                size="sm"
                iconName={loadingMore ? 'Loader2' : 'ChevronDown'}
                onClick={loadMoreLogs}
                disabled={!hasMore || loadingMore}
              >
                {loadingMore ? 'Loading more...' : hasMore ? 'Load older logs' : 'No more logs loaded'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
